import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import SectionHeader from '../SectionHeader';
import EmptyState from '../EmptyState';
import { format } from 'date-fns';

const FIELDS = [
  { icon: '📖', label: 'What Happened', field: 'summary', placeholder: 'Write the narrative of this session...', rows: 140 },
  { icon: '✨', label: 'Highlights & Memorable Moments', field: 'highlights', placeholder: 'The moments worth keeping...', rows: 110 },
  { icon: '🧵', label: 'Hooks for Next Time', field: 'hooks', placeholder: "What's unresolved? What still needs answering?", rows: 110 },
];

function RecapReadView({ recap, onEdit }) {
  const hasContent = recap.summary || recap.highlights || recap.hooks;

  return (
    <div className="bg-parchment-mid border-[1.5px] border-parchment-dark rounded-md p-8 shadow-card">
      <div className="flex items-start justify-between mb-6 pb-5 border-b-[1.5px] border-parchment-dark flex-wrap gap-4">
        <div className="flex-1">
          <div className="font-lora text-sm italic text-ink-light uppercase mb-1">
            Session {recap.session_number}
          </div>
          <h3 className="font-playfair text-2xl font-bold text-ink">
            {recap.session_title || <span className="italic text-ink-light font-normal">Untitled Session</span>}
          </h3>
        </div>
        <div className="flex flex-col items-end gap-2">
          {recap.updated_at && (
            <div className="font-lora text-[0.82rem] italic text-ink-light text-right whitespace-nowrap">
              last updated<br />
              {format(new Date(recap.updated_at), 'MMM d, h:mm a')}
            </div>
          )}
          <button 
            onClick={onEdit} 
            className="bg-transparent border border-parchment-dark rounded px-3 py-1 font-lora text-[0.78rem] text-ink-mid cursor-pointer transition-colors hover:bg-ink hover:text-parchment hover:border-ink"
          >
            ✎ edit
          </button>
        </div>
      </div>

      {!hasContent ? (
        <div className="text-center py-10 font-lora italic text-ink-light">
          Nothing written yet. Click <span className="not-italic font-medium text-ink-mid">edit</span> to add your notes.
        </div>
      ) : (
        <div className="space-y-7">
          {FIELDS.map(({ icon, label, field }) => {
            const value = recap[field];
            if (!value?.trim()) return null;
            return (
              <div key={field}>
                <div className="flex items-center gap-2 mb-3">
                  <span>{icon}</span>
                  <span className="font-playfair text-base font-bold text-ink">{label}</span>
                </div>
                <div className="font-lora text-[0.9rem] text-ink leading-[1.85] whitespace-pre-wrap border-l-[2px] border-dashed border-parchment-dark ml-1 pl-4">
                  {value}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecapEditForm({ recap, onSave, onCancel }) {
  const [form, setForm] = useState({
    session_title: recap.session_title || '',
    summary: recap.summary || '',
    highlights: recap.highlights || '',
    hooks: recap.hooks || '',
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setStatus('saving');
    const { error } = await supabase
      .from('recap')
      .update({ ...form, updated_at: new Date().toISOString() })
      .eq('id', recap.id);

    if (!error) {
      setStatus('saved');
      setTimeout(() => onSave(), 800);
    } else {
      setStatus('idle');
    }
  };

  return (
    <div className="bg-parchment-mid border-[1.5px] border-parchment-dark rounded-md p-8 shadow-card">
      <div className="flex items-start justify-between mb-6 pb-5 border-b-[1.5px] border-parchment-dark flex-wrap gap-4">
        <div className="flex-1">
          <div className="font-lora text-sm italic text-ink-light uppercase mb-1">
            Session {recap.session_number}
          </div>
          <input 
            className="font-playfair text-2xl font-bold text-ink bg-transparent border-none outline-none border-b-[1.5px] border-dashed border-parchment-dark pb-1 w-full max-w-[420px] focus:border-ink-mid placeholder:text-parchment-dark placeholder:italic transition-colors"
            placeholder="Give this session a title..."
            value={form.session_title}
            onChange={e => handleChange('session_title', e.target.value)}
            autoFocus
          />
        </div>
      </div>

      <div className="space-y-7">
        {FIELDS.map(({ icon, label, field, placeholder, rows }) => (
          <div key={field}>
            <div className="flex items-center gap-2 mb-2">
              <span>{icon}</span>
              <span className="font-playfair text-base font-bold text-ink">{label}</span>
            </div>
            <textarea 
              className="w-full bg-parchment/60 border-[1.5px] border-parchment-dark rounded px-4 py-3 font-lora text-[0.88rem] text-ink leading-relaxed outline-none resize-y transition-all focus:border-ink-mid focus:bg-parchment/90 placeholder:text-ink-light placeholder:italic"
              style={{ minHeight: rows }}
              placeholder={placeholder}
              value={form[field]}
              onChange={e => handleChange(field, e.target.value)}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-5 mt-7 border-t border-dashed border-parchment-dark flex-wrap gap-3">
        <span className={`font-lora text-[0.9rem] italic ${status === 'saving' ? 'text-theme-honey' : status === 'saved' ? 'text-theme-moss' : 'text-ink-light'}`}>
          {status === 'saving' ? '✦ Saving...' : status === 'saved' ? '✓ Saved' : ''}
        </span>
        <div className="flex gap-3">
          <button 
            onClick={onCancel}
            className="bg-transparent border-[1.5px] border-parchment-dark rounded px-4 py-2 font-lora text-[0.84rem] text-ink-mid cursor-pointer hover:bg-parchment-dark/30"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={status === 'saving'}
            className="bg-ink text-parchment border-none rounded px-5 py-2 font-lora text-[0.86rem] cursor-pointer hover:bg-ink-mid disabled:opacity-50 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecapTab({ recaps, onRefresh }) {
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [pendingRecap, setPendingRecap] = useState(null);

  useEffect(() => {
    if (recaps.length > 0 && !selectedId && !pendingRecap) {
      setSelectedId(recaps[0].id);
    }
  }, [recaps]);

  useEffect(() => {
    if (pendingRecap) {
      const found = recaps.find(r => r.id === pendingRecap.id);
      if (found) {
        setSelectedId(found.id);
        setPendingRecap(null);
        setEditing(true);
      }
    }
  }, [recaps, pendingRecap]);

  useEffect(() => {
    setEditing(false);
  }, [selectedId]);

  const handleNewSession = async () => {
    const nums = recaps.map(r => r.session_number);
    const n = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    
    const { data, error } = await supabase
      .from('recap')
      .insert([{
        session_number: n,
        session_title: '',
        summary: '',
        highlights: '',
        hooks: '',
        updated_at: new Date().toISOString(),
      }])
      .select()
      .single();

    if (data && !error) {
      setPendingRecap(data);
      setSelectedId(data.id);
      setEditing(true);
      onRefresh?.();
    }
  };

  const allRecaps = pendingRecap && !recaps.find(r => r.id === pendingRecap.id) 
    ? [pendingRecap, ...recaps] 
    : recaps;

  const selectedRecap = allRecaps.find(r => r.id === selectedId);

  if (allRecaps.length === 0) {
    return (
      <div className="pl-4">
        <SectionHeader 
          title="Session Recaps" 
          description="One shared record everyone reads and writes" 
          buttonText="New Session"
          onButtonClick={handleNewSession}
        />
        <EmptyState icon="📝" title="No recaps yet." hint="Start your first session record." />
      </div>
    );
  }

  return (
    <div className="pl-4">
      <SectionHeader 
        title="Session Recaps" 
        description="One shared record everyone reads and writes" 
        buttonText="New Session"
        onButtonClick={handleNewSession}
      />

      <div className="flex items-center gap-3 mb-7 flex-wrap">
        <span className="font-lora text-base italic text-ink-mid">Viewing:</span>
        <select 
          className="bg-parchment-mid border-[1.5px] border-parchment-dark rounded px-3 py-1.5 font-lora text-[0.86rem] text-ink outline-none cursor-pointer"
          value={selectedId || ''}
          onChange={e => setSelectedId(e.target.value)}
        >
          {allRecaps.map(r => (
            <option key={r.id} value={r.id}>
              Session {r.session_number}{r.session_title ? ` — ${r.session_title}` : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedRecap && (
        editing ? (
          <RecapEditForm 
            recap={selectedRecap} 
            onSave={() => { setEditing(false); onRefresh?.(); }} 
            onCancel={() => { setEditing(false); }} 
          />
        ) : (
          <RecapReadView 
            recap={selectedRecap} 
            onEdit={() => setEditing(true)} 
          />
        )
      )}
    </div>
  );
}